/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryResult,
  UseMutationResult
} from "react-query";
import { useDispatch } from "react-redux";

import { ApiResponse } from "../../interfaces/Api";
import { Bed, BedFormValue, OccupiedBed } from "../../interfaces/BedInterface";
import { notificationAdd } from "../../actions/notification";
import useCurrentResourceCentre from "../useCurrentResourceCentre";
import { getBeds, postBeds, patchBed, deleteBed, getOccupiedBeds } from "../../api/bed";

export const bedsQueryKey = "beds";
export const occupiedBedsQueryKey = "occupiedBeds";

// use beds
export const useBeds = (props?: {
  roomIds?: Array<number>;
  active?: boolean;
}): UseQueryResult<Array<Bed>, Error> => {
  const resourceCentre = useCurrentResourceCentre();

  return useQuery({
    queryKey: [bedsQueryKey, resourceCentre?.id, props?.active, props?.roomIds],
    queryFn: async () => {
      if (!resourceCentre?.id) return [];

      const response = await getBeds(props);
      return response.data || [];
    },
    enabled: !!resourceCentre?.id,
    staleTime: 5 * 60 * 1000
  });
};

// use occupied beds
export const useOccupiedBeds = (props?: {
  roomId: number;
}): UseQueryResult<Array<OccupiedBed>, Error> => {
  const resourceCentre = useCurrentResourceCentre();

  return useQuery({
    queryKey: [occupiedBedsQueryKey, resourceCentre?.id, props?.roomId],
    queryFn: async () => {
      if (!resourceCentre?.id) return [];

      const response = await getOccupiedBeds({ ...props });
      return response.data || [];
    },
    enabled: !!resourceCentre?.id
  });
};

// use post bed
export const usePostBed = (): UseMutationResult<
  ApiResponse<Array<Bed>>,
  Error,
  BedFormValue,
  unknown
> => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const resourceCentre = useCurrentResourceCentre();

  return useMutation({
    mutationFn: (data: BedFormValue) =>
      postBeds({ ...data, resourceCentreId: Number(resourceCentre?.id) }),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [bedsQueryKey, resourceCentre?.id] });
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: "Bed(s) created successfully",
          variant: "success"
        })
      );
    },
    onError: (error: any) => {
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: error?.message || "Failed to create bed(s)",
          variant: "error"
        })
      );
    }
  });
};

// use patch bed
export const usePatchBed = (): UseMutationResult<
  ApiResponse<Bed>,
  Error,
  { id: number | string; data: Partial<BedFormValue> },
  unknown
> => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const resourceCentre = useCurrentResourceCentre();

  return useMutation({
    mutationFn: ({ id, data }) => patchBed(Number(id), data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [bedsQueryKey, resourceCentre?.id] });
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: "Bed updated successfully",
          variant: "success"
        })
      );
    },
    onError: (error: any) => {
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: error?.message || "Failed to update bed",
          variant: "error"
        })
      );
    }
  });
};

// use delete bed (soft delete)
export const useSoftDeleteBed = (): UseMutationResult<
  ApiResponse<null>,
  Error,
  number | string,
  unknown
> => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const resourceCentre = useCurrentResourceCentre();

  return useMutation({
    mutationFn: (id) => deleteBed(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [bedsQueryKey, resourceCentre?.id] });
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: "Bed deleted successfully",
          variant: "success"
        })
      );
    },
    onError: (error: any) => {
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: error?.message || "Failed to delete bed",
          variant: "error"
        })
      );
    }
  });
};
